/**
 * Lobby (online room or offline setup — same screen): room code + invite link, slots with
 * host controls, region pick on a small world map (only the lobby's region set is in play;
 * teams with a fixed layout show it instead of a picker), ready / start.
 * Quick play is matchmaking, not a room: `FINDING PLAYERS · 2/8`, the humans so far, the
 * empty seats as one line with the bot fill countdown, and CANCEL — no code, READY or START.
 */
import type { LoadedWorld } from '@sa/mapdata';
import type { BotDifficulty, MatchSettings, PlayerSlot, RegionId } from '@sa/sim';
import { COLORS, ENEMY_STYLES, cssRgb } from '../../theme';
import { copyText, h, kids } from '../dom';
import { drawWorld, regionAt, type RegionMark } from '../worldmap';

export interface LobbyModel {
  /** room code, null for an offline match */
  code: string | null;
  slots: PlayerSlot[];
  settings: MatchSettings;
  /** my slot index */
  me: number;
  isHost: boolean;
  /** the host's slot (the host never needs READY), -1 if unknown */
  hostSlot: number;
  /** the regions this lobby plays on (`room.state.regionSet`); others are not pickable */
  regionSet: readonly RegionId[];
  /** teams with a fixed layout: each team's regions (index = team − 1); no picking */
  teamRegions?: readonly (readonly RegionId[])[] | null;
  /** quick play: when the empty seats fill with bots (epoch ms; null before the first join) and whether the countdown runs */
  quickplay?: { fillAt: number | null; starting: boolean } | null;
  setSlot(slot: number, kind: 'open' | 'closed' | 'bot', difficulty?: BotDifficulty, team?: number): void;
  pickRegion(id: RegionId | null): void;
  ready(r: boolean): void;
  start(): void;
  leave(): void;
}

const DIFFS: BotDifficulty[] = ['easy', 'normal', 'hard'];

export class LobbyScreen {
  readonly el: HTMLDivElement;
  private slotsEl = h('div.slots');
  private map = h('canvas.mapPick');
  private mapTitle = h('div.sub', { style: { marginBottom: '0', textAlign: 'left' } }, 'PICK YOUR START REGION');
  private actions = h('div.row-inline');
  private waiting = h('div.sub.wait');
  private hover: RegionId | null = null;
  private m: LobbyModel | null = null;
  private onResize = () => this.drawMap();
  /** the room code the code line was built for (rebuilt only when it changes: a click in flight survives updates) */
  private codeShown: string | null = null;
  /** quick play: ticks the bot fill countdown */
  private tick: ReturnType<typeof setInterval> | null = null;

  constructor(
    private world: LoadedWorld,
    private err: HTMLElement,
  ) {
    this.map.addEventListener('mousemove', (e) => {
      const r = this.regionFromEvent(e);
      if (r !== this.hover) {
        this.hover = r;
        this.drawMap();
      }
    });
    this.map.addEventListener('click', (e) => {
      const r = this.regionFromEvent(e);
      const m = this.m;
      if (!m || r === null) return;
      const mine = m.slots.find((s) => s.slot === m.me);
      const taken = m.slots.some((s) => s.slot !== m.me && s.regionId === r);
      if (!taken) m.pickRegion(mine?.regionId === r ? null : r);
    });
    this.el = h(
      'div.screen.nobg',
      {},
      h(
        'div.lobby',
        {},
        h('div.col', { style: { width: '100%' } }, h('div.title.code'), this.slotsEl, this.actions, this.waiting, this.err),
        h('div.col', { style: { width: '100%' } }, this.mapTitle, this.map),
      ),
    );
    window.addEventListener('resize', this.onResize);
  }

  dispose(): void {
    window.removeEventListener('resize', this.onResize);
    if (this.tick) clearInterval(this.tick);
    this.tick = null;
  }

  private regionFromEvent(e: MouseEvent): RegionId | null {
    const r = this.map.getBoundingClientRect();
    const s = Math.min(r.width / this.world.width, r.height / this.world.height);
    const ox = (r.width - this.world.width * s) / 2;
    const oy = (r.height - this.world.height * s) / 2;
    const id = regionAt(this.world, (e.clientX - r.left - ox) / s, (e.clientY - r.top - oy) / s);
    // only the lobby's region set can be picked, and nothing when teams have fixed regions
    const m = this.m;
    return id !== null && m && !m.teamRegions && m.regionSet.includes(id) ? id : null;
  }

  update(m: LobbyModel): void {
    this.m = m;
    if (m.quickplay) {
      this.updateQuickplay(m, m.quickplay);
      return;
    }
    const codeEl = this.el.querySelector('.code') as HTMLElement;
    if (codeEl.classList.contains('qp') || this.codeShown !== (m.code ?? '')) {
      codeEl.classList.remove('qp');
      this.codeShown = m.code ?? '';
      if (m.code) {
        const code = m.code;
        codeEl.replaceChildren(
          h('span', { style: { marginRight: '18px' } }, code),
          copyButton('COPY CODE', () => code),
          copyButton('COPY LINK', () => `${location.origin}${location.pathname}?room=${code}`),
        );
      } else codeEl.replaceChildren(h('span', {}, 'OFFLINE'));
    }

    this.slotsEl.replaceChildren(...m.slots.map((s) => this.slotRow(m, s)));

    const mine = m.slots.find((s) => s.slot === m.me);
    const block = startBlocker(m);
    this.actions.replaceChildren(
      ...kids(
        h('button.btn.ghost', { onclick: () => m.leave() }, 'LEAVE'),
        // the host's ready is implied by START
        m.code && mine && !m.isHost ? h(`button.btn${mine.ready ? '.on' : ''}`, { onclick: () => m.ready(!mine.ready) }, mine.ready ? 'READY ☑' : 'READY ☐') : null,
        m.isHost ? h('button.btn.primary', { disabled: block !== null, onclick: () => m.start() }, 'START') : null,
      ),
    );
    this.waiting.textContent = m.isHost ? (block ?? '') : mine?.ready ? 'WAITING FOR THE HOST TO START' : 'MARK READY WHEN SET';
    const pick = this.world.regions.find((r) => r.id === mine?.regionId)?.name;
    this.mapTitle.textContent = m.teamRegions
      ? 'TEAM REGIONS · FIXED FOR THIS LAYOUT'
      : `YOUR REGION · ${pick ?? 'RANDOM — PICK ON THE MAP'} · ${m.regionSet.length} IN PLAY`;
    this.map.style.cursor = m.teamRegions ? 'default' : 'pointer';
    this.drawMap();
  }

  /** Quick play: matchmaking, not a room. */
  private updateQuickplay(m: LobbyModel, qp: NonNullable<LobbyModel['quickplay']>): void {
    const humans = m.slots.filter((s) => s.kind === 'human');
    const seats = m.slots.filter((s) => s.kind === 'open').length;
    const codeEl = this.el.querySelector('.code') as HTMLElement;
    this.codeShown = null;
    codeEl.classList.add('qp');
    codeEl.replaceChildren(h('span', {}, qp.starting ? 'MATCH FOUND' : 'FINDING PLAYERS'), h('span.count', {}, `${humans.length}/${m.slots.length}`));
    const fill = h('span.fill');
    const rows = humans.map((s) => h('div.slot-row', {}, h('span.n', {}, '·'), h('span', {}, s.name.toUpperCase()), h('div.ctl', {}, s.slot === m.me ? h('span.ready', {}, 'YOU') : null)));
    if (seats) rows.push(h('div.slot-row.seats', {}, h('span.n', {}, '+'), h('span.open', {}, `${seats} SEAT${seats > 1 ? 'S' : ''} · BOTS FILL IN `, fill), h('div.ctl')));
    this.slotsEl.replaceChildren(...rows);
    this.actions.replaceChildren(h('button.btn.ghost', { onclick: () => m.leave() }, 'CANCEL'));
    this.waiting.textContent = qp.starting ? 'STARTING' : 'THE MATCH STARTS BY ITSELF';
    const pick = this.world.regions.find((r) => r.id === m.slots.find((s) => s.slot === m.me)?.regionId)?.name;
    this.mapTitle.textContent = `YOUR REGION · ${pick ?? 'RANDOM — PICK ON THE MAP'}`;
    this.map.style.cursor = 'pointer';
    const paint = () => {
      const left = qp.fillAt === null ? null : Math.max(0, Math.ceil((qp.fillAt - Date.now()) / 1000));
      fill.textContent = qp.starting || left === 0 ? 'NOW' : left === null ? '—' : `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
    };
    paint();
    if (this.tick) clearInterval(this.tick);
    this.tick = setInterval(paint, 250);
    this.drawMap();
  }

  private slotRow(m: LobbyModel, s: PlayerSlot): HTMLElement {
    const label =
      s.kind === 'human'
        ? s.name.toUpperCase()
        : s.kind === 'bot'
          ? `BOT · ${(s.difficulty ?? 'normal').toUpperCase()}`
          : s.kind === 'open'
            ? 'OPEN'
            : 'CLOSED';
    const ctl = h('div.ctl');
    // host: the select is the name cell (no duplicate "BOT · NORMAL" label)
    let nameCell: HTMLElement | null = null;
    if (m.isHost && s.kind !== 'human') {
      const sel = h(
        'select',
        {},
        ...['open', 'closed', ...DIFFS.map((d) => `bot:${d}`)]
          .filter((o) => m.code || o !== 'open')
          .map((o) => h('option', { value: o }, o.replace('bot:', 'bot · ').toUpperCase())),
      );
      sel.value = s.kind === 'bot' ? `bot:${s.difficulty ?? 'normal'}` : s.kind;
      sel.onchange = () => {
        const v = sel.value;
        // the server only takes a team in teams mode (FFA → badTeam)
        const team = m.settings.mode === 'teams' ? s.team : undefined;
        if (v.startsWith('bot:')) m.setSlot(s.slot, 'bot', v.slice(4) as BotDifficulty, team);
        else m.setSlot(s.slot, v as 'open' | 'closed', undefined, team);
      };
      nameCell = h('span', { className: s.kind === 'bot' ? 'namesel' : 'namesel open' }, sel);
    }
    if (m.settings.mode === 'teams' && m.isHost && s.kind !== 'closed' && s.kind !== 'open') {
      const t = h('select', {}, ...Array.from({ length: m.settings.teamCount ?? 2 }, (_, i) => h('option', { value: String(i + 1) }, `TEAM ${i + 1}`)));
      t.value = String(s.team);
      t.onchange = () => m.setSlot(s.slot, s.kind === 'bot' ? 'bot' : 'open', s.difficulty, Number(t.value));
      if (s.kind === 'bot') ctl.append(t);
    }
    // teams: which side each seat is on (the host's bot rows have a picker instead)
    if (m.settings.mode === 'teams' && s.kind !== 'closed' && s.kind !== 'open' && !(m.isHost && s.kind === 'bot')) {
      const myTeam = m.slots.find((q) => q.slot === m.me)?.team;
      ctl.append(h('span.team', { style: { color: cssRgb(s.team === myTeam ? COLORS.me : ENEMY_STYLES[0].color) } }, `T${s.team}`));
    }
    const status = s.kind === 'human' ? humanStatus(m, s) : null;
    if (status?.text) ctl.append(h('span', { className: status.cls }, status.text));
    const cls = s.kind === 'open' || s.kind === 'closed' ? 'open' : '';
    return h('div.slot-row', {}, h('span.n', {}, String(s.slot + 1)), nameCell ?? h('span', { className: cls }, label), ctl);
  }

  private drawMap(): void {
    const m = this.m;
    if (!m) return;
    const marks: RegionMark[] = [];
    let enemy = 0;
    const myTeam = m.slots.find((s) => s.slot === m.me)?.team;
    for (const reg of this.world.regions) {
      if (m.teamRegions) {
        const t = m.teamRegions.findIndex((g) => g.includes(reg.id));
        if (t < 0) marks.push({ id: reg.id, color: COLORS.neutral, off: true });
        else marks.push({ id: reg.id, color: t + 1 === myTeam ? COLORS.me : ENEMY_STYLES[0].color, label: `${reg.name} · T${t + 1}` });
        continue;
      }
      if (!m.regionSet.includes(reg.id)) {
        marks.push({ id: reg.id, color: COLORS.neutral, off: true });
        continue;
      }
      const owner = m.slots.find((s) => s.regionId === reg.id);
      if (owner?.slot === m.me) marks.push({ id: reg.id, color: COLORS.me, label: reg.name });
      else if (owner) marks.push({ id: reg.id, color: ENEMY_STYLES[enemy++ % ENEMY_STYLES.length].color, label: `${reg.name} · ${owner.name.toUpperCase()}` });
      else marks.push({ id: reg.id, color: COLORS.neutral, label: reg.name, dimLabel: true });
    }
    drawWorld(this.map, this.world, { alpha: 0.4, marks, hover: this.hover });
  }
}

/** Status word for a human row: host and you are never "not ready". */
function humanStatus(m: LobbyModel, s: PlayerSlot): { text: string; cls: string } {
  const host = s.slot === m.hostSlot;
  // offline there is no one to host for (and the row may already say YOU)
  if (s.slot === m.me && !m.code) return { text: s.name.toUpperCase() === 'YOU' ? '' : 'YOU', cls: 'ready' };
  if (s.slot === m.me) return { text: host ? 'YOU · HOST' : s.ready ? 'YOU · READY' : 'YOU', cls: 'ready' };
  if (host) return { text: 'HOST', cls: 'ready' };
  return s.ready ? { text: 'READY', cls: 'ready' } : { text: 'NOT READY', cls: 'open' };
}

/** Why START is not available yet (null: it is). Mirrors the server's rule. */
export function startBlocker(m: Pick<LobbyModel, 'slots' | 'hostSlot' | 'code' | 'settings'>): string | null {
  const occ = m.slots.filter((s) => s.kind === 'human' || s.kind === 'bot');
  if (occ.length < 2) return 'ADD A BOT OR WAIT FOR A PLAYER';
  if (m.code) {
    const waiting = m.slots.filter((s) => s.kind === 'human' && !s.ready && s.slot !== m.hostSlot).map((s) => s.name.toUpperCase());
    if (waiting.length) return `WAITING FOR ${waiting.length > 2 ? `${waiting.length} PLAYERS` : waiting.join(' + ')}`;
  }
  if (m.settings.mode === 'teams' && new Set(occ.map((s) => s.team)).size < 2) return 'NEED PLAYERS ON 2 TEAMS';
  return null;
}

/** A button that copies `text()` and says whether it worked (then reads `label` again). */
function copyButton(label: string, text: () => string): HTMLButtonElement {
  const b = h('button.act', { style: { marginLeft: '8px' } }, label);
  let reset: ReturnType<typeof setTimeout> | undefined;
  b.onclick = () => {
    void copyText(text()).then((ok) => {
      b.textContent = ok ? 'COPIED' : 'COPY FAILED';
      clearTimeout(reset);
      reset = setTimeout(() => (b.textContent = label), 1500);
    });
  };
  return b;
}
